import React, { useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import dayjs from "dayjs";
import { Upload } from "react-ikonate";
import { Modal } from "@wwg/ui";
import ReactModal from "react-modal";

import { useIBOSearch } from "shared/@hooks/LookupByIBO/useIBOSearch";
import SuggestionInput from "shared/@forms/SuggestionInput";
import { useMemberInfo } from "../useIncidents";

import Input from "shared/@forms/Input";
import PhoneInput from "shared/@forms/PhoneInput";
import { Select } from "shared/Select/Select";
import TextArea from "shared/@forms/TextArea";
import Button from "shared/Button";
import Loader from "shared/Loader";

import styles from "./CreateNewReportModal.module.css";

/**Modal with a form to create a new incident report. Opens when create new is clicked on the home page.**/
export function CreateNewReportModal({ onCreate, isOpen, onClose }) {
  const { fetchAddress, fetchMember } = useMemberInfo();
  const [primaryIsLoading, setPrimaryIsLoading] = useState(false);
  const [complaintIsLoading, setComplaintIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const { searchResults, isLoading } = useIBOSearch(searchTerm);
  const [primaryAutofill, setPrimaryAutofill] = useState(false);
  const [ComplaintAutofill, setComplaintAutofill] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [daysInMonth, setDaysInMonth] = useState([]);
  const [yearList, setYearList] = useState([]);
  const [formData, setFormData] = useState({
    primaryIboNumber: "",
    firstName: "",
    lastName: "",
    phoneNumber: "",
    primaryEmail: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
    problemType: "Select",
    problemLocation: "Select",
    subLocation: "Select",
    description: "",
    evidence: [],
    startDate: {
      month: "Month",
      day: "Day",
      year: "Year",
    },
    complaintAgainst: {
      iboNumber: "",
      firstName: "",
      lastName: "",
      username: "",
      phoneNumber: "",
      email: "",
      address: "",
      city: "",
      state: "",
      zipCode: "",
    },
    isMember: "Select",
    hasViolated: "Select",
    fromUpline: "Select",
  });
  const [errors, setErrors] = useState({
    firstName: "",
    lastName: "",
    phoneNumber: "",
    primaryEmail: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
    problemType: "",
    problemLocation: "",
    description: "",
    evidence: [],
    startDate: "",
    complaintAgainst: {
      firstName: "",
      lastName: "",
      phoneNumber: "",
      email: "",
      address: "",
      city: "",
      state: "",
      zipCode: "",
    },
    isMember: "",
    hasViolated: "",
    fromUpline: "",
  });

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  //Creates an array that contains the year +- 2 years
  const populateYears = () => {
    const currentYear = dayjs().format("YYYY") - 2;
    const yearArray = Array(5)
      .fill()
      .map((_, i) => currentYear + i);
    setYearList(yearArray);
  };

  //Populates an array with the number of days in the selected month
  const populateDaysInMonth = (month) => {
    const daysInMonth = dayjs().month(months.indexOf(month)).daysInMonth();
    const dayArray = Array(daysInMonth)
      .fill()
      .map((_, i) => i + 1);
    setDaysInMonth(dayArray);
  };

  const convertToBase64 = async (file) => {
    return new Promise((resolve, reject) => {
      let fileReader = new FileReader();
      fileReader.onload = function () {
        return resolve({
          data: fileReader.result,
          name: file.name,
          mimetype: file.type,
        });
      };
      fileReader.readAsDataURL(file);
    });
  };

  //Handles form input in state
  const handleForm = async (e, field) => {
    if (field !== "evidence") {
      if (field) {
        switch (field) {
          case "startDate.month":
            setFormData({
              ...formData,
              startDate: {
                ...formData.startDate,
                month: e,
              },
            });
            populateDaysInMonth(e);
            break;
          case "startDate.day":
            setFormData({
              ...formData,
              startDate: {
                ...formData.startDate,
                day: e,
              },
            });
            populateYears();
            break;
          case "startDate.year":
            setFormData({
              ...formData,
              startDate: {
                ...formData.startDate,
                year: e,
              },
            });
            break;
          case "complaintAgainst.firstName":
            setFormData({
              ...formData,
              complaintAgainst: {
                ...formData.complaintAgainst,
                firstName: e,
              },
            });
            break;
          case "complaintAgainst.lastName":
            setFormData({
              ...formData,
              complaintAgainst: {
                ...formData.complaintAgainst,
                lastName: e,
              },
            });
            break;
          case "complaintAgainst.phoneNumber":
            setFormData({
              ...formData,
              complaintAgainst: {
                ...formData.complaintAgainst,
                phoneNumber: e,
              },
            });
            break;
          case "complaintAgainst.address":
            setFormData({
              ...formData,
              complaintAgainst: {
                ...formData.complaintAgainst,
                address: e,
              },
            });
            break;
          case "complaintAgainst.city":
            setFormData({
              ...formData,
              complaintAgainst: {
                ...formData.complaintAgainst,
                city: e,
              },
            });
            break;
          case "complaintAgainst.state":
            setFormData({
              ...formData,
              complaintAgainst: {
                ...formData.complaintAgainst,
                state: e,
              },
            });
            break;
          case "complaintAgainst.zipCode":
            setFormData({
              ...formData,
              complaintAgainst: {
                ...formData.complaintAgainst,
                zipCode: e,
              },
            });
            break;
          default:
            setFormData({ ...formData, [field]: e });
            break;
        }
      } else {
        if (e.target.id === "state") {
          setFormData({
            ...formData,
            [e.target.id]: e.target.value,
          });
        }
        setFormData({ ...formData, [e.target.id]: e.target.value });
      }
    } else {
      const files = [...e.target.files];
      var filePromises = await Promise.all(
        files.map((f) => {
          return convertToBase64(f);
        })
      );
      setFormData({ ...formData, [field]: filePromises });
      const fileNames = files.map((file) => file.name);
      setFileList([...fileNames]);
    }
  };

  //Function to check if the error object is clear to submit or not
  const checkValidity = (errors) => {
    let isValid = true;
    for (let error in errors) {
      if (error === "complaintAgainst") {
        for (let subError in errors[error]) {
          if (errors[error][subError]) isValid = false;
        }
      } else if (errors[error]) {
        isValid = false;
      }
    }
    return isValid;
  };

  //Error checking happens here - Most just check for content but the zip code and state both use RegEx
  const handleSubmit = () => {
    let tempErrors = {};
    if (formData.firstName.length === 0) {
      tempErrors = { ...tempErrors, firstName: "Please enter a first name" };
    } else if (!primaryAutofill) {
      tempErrors = {
        ...tempErrors,
        firstName: "Use Autofill to populate section",
      };
    } else {
      tempErrors = { ...tempErrors, firstName: "" };
    }
    if (formData.lastName.length === 0)
      tempErrors = { ...tempErrors, lastName: "Please enter a last name" };
    else tempErrors = { ...tempErrors, lastName: "" };
    if (formData.phoneNumber.length === 0)
      tempErrors = {
        ...tempErrors,
        phoneNumber: "Please enter a phone number",
      };
    else tempErrors = { ...tempErrors, phoneNumber: "" };
    if (formData.address.length === 0)
      tempErrors = { ...tempErrors, address: "Please enter an address" };
    else tempErrors = { ...tempErrors, address: "" };
    if (formData.city.length === 0)
      tempErrors = { ...tempErrors, city: "Please enter a City" };
    else tempErrors = { ...tempErrors, city: "" };
    if (
      formData.state.length === 0 ||
      !/^(?:(A[KLRZB]|BC|C[AOT]|DE|FL|GA|HI|I[ADLN]|K[SY]|LA|M[ADEINOSTB]|N[CDEHJMVYBLT]|O[NHKR]|P[AER]|QC|RI|S[CDK]|T[NX]|UT|V[AT]|W[AIVY]|YT))$/.test(
        formData.state.toUpperCase()
      )
    )
      tempErrors = { ...tempErrors, state: "Please enter a two letter state" };
    else tempErrors = { ...tempErrors, state: "" };
    if (
      formData.zipCode.length === 0 ||
      !/^(\d{5}-\d{4}|\d{5}|\d{9})$|^([a-zA-Z]\d[a-zA-Z]( )?\d[a-zA-Z]\d)$/.test(
        formData.zipCode
      )
    )
      tempErrors = { ...tempErrors, zipCode: "Please enter a Zip code" };
    else tempErrors = { ...tempErrors, zipCode: "" };
    if (formData.problemType === "Select")
      tempErrors = { ...tempErrors, problemType: "Please select an option" };
    else tempErrors = { ...tempErrors, problemType: "" };
    if (formData.problemLocation === "Select")
      tempErrors = {
        ...tempErrors,
        problemLocation: "Please select an option",
      };
    else tempErrors = { ...tempErrors, problemLocation: "" };
    if (formData.description.length === 0)
      tempErrors = { ...tempErrors, description: "Please enter a description" };
    else tempErrors = { ...tempErrors, description: "" };
    if (formData.evidence.length === 0)
      tempErrors = { ...tempErrors, evidence: "Please upload your evidence" };
    else tempErrors = { ...tempErrors, evidence: "" };
    if (
      formData.startDate.month === "Month" ||
      formData.startDate.day === "Day" ||
      formData.startDate.year === "Year"
    )
      tempErrors = { ...tempErrors, startDate: "Please enter a date" };
    else tempErrors = { ...tempErrors, startDate: "" };

    //Complaint against section
    if (formData.complaintAgainst.firstName.length === 0) {
      tempErrors = {
        ...tempErrors,
        complaintAgainst: {
          ...tempErrors.complaintAgainst,
          firstName: "Please enter a first name",
        },
      };
    } else if (!ComplaintAutofill) {
      tempErrors = {
        ...tempErrors,
        complaintAgainst: {
          ...tempErrors.complaintAgainst,
          firstName: "Use Autofill to populate section",
        },
      };
    } else {
      tempErrors = {
        ...tempErrors,
        complaintAgainst: {
          ...tempErrors.complaintAgainst,
          firstName: "",
        },
      };
    }

    if (formData.complaintAgainst.lastName.length === 0)
      tempErrors = {
        ...tempErrors,
        complaintAgainst: {
          ...tempErrors.complaintAgainst,
          lastName: "Please enter a last name",
        },
      };
    else
      tempErrors = {
        ...tempErrors,
        complaintAgainst: {
          ...tempErrors.complaintAgainst,
          lastName: "",
        },
      };
    if (formData.complaintAgainst.phoneNumber.length === 0)
      tempErrors = {
        ...tempErrors,
        complaintAgainst: {
          ...tempErrors.complaintAgainst,
          phoneNumber: "Please enter a phone number",
        },
      };
    else
      tempErrors = {
        ...tempErrors,
        complaintAgainst: {
          ...tempErrors.complaintAgainst,
          phoneNumber: "",
        },
      };
    if (formData.complaintAgainst.address.length === 0)
      tempErrors = {
        ...tempErrors,
        complaintAgainst: {
          ...tempErrors.complaintAgainst,
          address: "Please enter an address",
        },
      };
    else
      tempErrors = {
        ...tempErrors,
        complaintAgainst: {
          ...tempErrors.complaintAgainst,
          address: "",
        },
      };
    if (formData.complaintAgainst.city.length === 0)
      tempErrors = {
        ...tempErrors,
        complaintAgainst: {
          ...tempErrors.complaintAgainst,
          city: "Please enter a City",
        },
      };
    else
      tempErrors = {
        ...tempErrors,
        complaintAgainst: {
          ...tempErrors.complaintAgainst,
          city: "",
        },
      };
    if (
      formData.complaintAgainst.state.length === 0 ||
      !/^(?:(A[KLRZB]|BC|C[AOT]|DE|FL|GA|HI|I[ADLN]|K[SY]|LA|M[ADEINOSTB]|N[CDEHJMVYBLT]|O[NHKR]|P[AER]|QC|RI|S[CDK]|T[NX]|UT|V[AT]|W[AIVY]|YT))$/.test(
        formData.complaintAgainst.state.toUpperCase()
      )
    )
      tempErrors = {
        ...tempErrors,
        complaintAgainst: {
          ...tempErrors.complaintAgainst,
          state: "Please enter a two letter state",
        },
      };
    else
      tempErrors = {
        ...tempErrors,
        complaintAgainst: {
          ...tempErrors.complaintAgainst,
          state: "",
        },
      };
    if (
      formData.complaintAgainst.zipCode.length === 0 ||
      !/^(\d{5}-\d{4}|\d{5}|\d{9})$|^([a-zA-Z]\d[a-zA-Z]( )?\d[a-zA-Z]\d)$/.test(
        formData.complaintAgainst.zipCode
      )
    )
      tempErrors = {
        ...tempErrors,
        complaintAgainst: {
          ...tempErrors.complaintAgainst,
          zipCode: "Please enter a Zip Code",
        },
      };
    else
      tempErrors = {
        ...tempErrors,
        complaintAgainst: {
          ...tempErrors.complaintAgainst,
          zipCode: "",
        },
      };

    if (formData.isMember === "Select")
      tempErrors = { ...tempErrors, isMember: "Please select an option" };
    else tempErrors = { ...tempErrors, isMember: "" };
    if (formData.hasViolated === "Select")
      tempErrors = {
        ...tempErrors,
        hasViolated: "Please select an option",
      };
    else tempErrors = { ...tempErrors, hasViolated: "" };
    if (formData.fromUpline === "Select")
      tempErrors = { ...tempErrors, fromUpline: "Please select an option" };
    else tempErrors = { ...tempErrors, fromUpline: "" };
    setErrors(tempErrors);
    checkValidity(tempErrors);
    if (checkValidity(tempErrors)) {
      onCreate(formData);
      clearForm();
    }
  };

  //Fills fields based off autoComplete selections
  const handleFillForm = async (index, isPrimary) => {
    if (isPrimary) {
      setPrimaryIsLoading(true);
    } else {
      setComplaintIsLoading(true);
    }

    const [address] = await fetchAddress(index.iboNumber);
    const memberInfo = await fetchMember(index.iboNumber);
    const hasAddress = address === undefined ? false : true;

    let member = [];

    if (index.firstName === memberInfo.primaryInfo.iboA?.firstName) {
      member = memberInfo.primaryInfo.iboA;
    } else if (index.firstName === memberInfo.primaryInfo.iboB?.firstName) {
      member = memberInfo.primaryInfo.iboB;
    }

    if (isPrimary && !hasAddress) {
      setFormData({
        ...formData,
        address: "",
        city: "",
        state: "",
        zipCode: "",
        primaryIboNumber: index.iboNumber,
        firstName: member.firstName,
        lastName: member.lastName,
        phoneNumber: member.phone || "",
        primaryEmail: member.email || "notprovided@email.com",
      });
      setPrimaryAutofill(true);
    }

    if (isPrimary && hasAddress) {
      setFormData({
        ...formData,
        address: address.line1,
        city: address.city,
        state: address.state,
        zipCode: address.postalCode,
        primaryIboNumber: index.iboNumber,
        firstName: member.firstName,
        lastName: member.lastName,
        phoneNumber: member.phone || "",
        primaryEmail: member.email || "notprovided@email.com",
      });
      setPrimaryAutofill(true);
    }

    if (!isPrimary && !hasAddress) {
      setFormData({
        ...formData,
        complaintAgainst: {
          address: "",
          city: "",
          state: "",
          zipCode: "",
          iboNumber: index.iboNumber,
          firstName: member.firstName,
          lastName: member.lastName,
          username: memberInfo.primaryInfo.username,
          phoneNumber: member.phone || "",
          email: member.email || "notprovided@email.com",
        },
      });
      setComplaintAutofill(true);
    }

    if (!isPrimary && hasAddress) {
      setFormData({
        ...formData,
        complaintAgainst: {
          address: address.line1,
          city: address.city,
          state: address.state,
          zipCode: address.postalCode,
          iboNumber: index.iboNumber,
          firstName: member.firstName,
          lastName: member.lastName,
          username: memberInfo.primaryInfo.username,
          phoneNumber: member.phone || "",
          email: member.email || "notprovided@email.com",
        },
      });
      setComplaintAutofill(true);
    }
    setPrimaryIsLoading(false);
    setComplaintIsLoading(false);
  };

  const handleCancel = () => {
    clearForm();
    onClose();
  };

  const clearForm = () => {
    setFormData({
      ...formData,
      primaryIboNumber: "",
      firstName: "",
      lastName: "",
      phoneNumber: "",
      primaryEmail: "",
      address: "",
      city: "",
      state: "",
      zipCode: "",
      problemType: "Select",
      problemLocation: "Select",
      subLocation: "Select",
      description: "",
      evidence: [],
      startDate: {
        month: "Month",
        day: "Day",
        year: "Year",
      },
      complaintAgainst: {
        iboNumber: "",
        firstName: "",
        lastName: "",
        username: "",
        phoneNumber: "",
        email: "",
        address: "",
        city: "",
        state: "",
        zipCode: "",
      },
      isMember: "Select",
      hasViolated: "Select",
      fromUpline: "Select",
    });
    setFileList([]);
    setPrimaryAutofill(false);
    setComplaintAutofill(false);
  };

  return (
    <ReactModal
      isOpen={isOpen}
      className={styles.createNewReportModalBody}
      overlayClassName={styles.overlay}
      shouldCloseOnOverlayClick={false}
      shouldCloseOnOverlayEsc={false}
      onRequestClose={onClose}
    >
      <div className={styles.title}>Create New Report</div>
      <div className={styles.hr} />

      <div className={styles.primaryInfo}>
        <span className={styles.label}>Primary Info</span>
        <span className={styles.loader}>
          <div>{primaryIsLoading && <Loader size={35} />}</div>
        </span>

        <span className={styles.nameAndNumber}>
          <span className={classNames(styles.third, styles.suggestionInput)}>
            <SuggestionInput
              value={formData.firstName || ""}
              autoFocus={false}
              minLength={3}
              debounceTime={500}
              fetchSuggestions={setSearchTerm}
              suggestions={searchResults}
              isLoading={isLoading}
              placeholder="First Name"
              id="firstName"
              onChange={(e) => {
                handleForm(e);
              }}
            >
              {(item, closePanel) => {
                const [, label, rest] = item.display.match(/^(.+):(.*)$/);

                return (
                  <div key={item.contactPK} className={styles.iboListItem}>
                    <span
                      onClick={(e) => {
                        handleFillForm(item, true);
                        setSearchTerm("");
                        closePanel();
                      }}
                    >
                      {item.firstName} {item.lastName} ({item.iboNumber})
                    </span>
                  </div>
                );
              }}
            </SuggestionInput>
          </span>
          <span className={styles.third}>
            <Input
              className={styles.input}
              autoComplete="off"
              placeholder="Last Name"
              id="lastName"
              value={formData.lastName || ""}
              onChange={(e) => handleForm(e)}
            />
          </span>
          <PhoneInput
            className={classNames(styles.third, styles.input)}
            autoComplete="off"
            placeholder="Phone Number"
            value={formData.phoneNumber || ""}
            id="phoneNumber"
            onChange={(e) => handleForm(e)}
          />
        </span>
        <span
          style={{
            height: "10px",
            display: "grid",
            gridAutoColumns: "34%",
            width: "100%",
            color: "red",
          }}
        >
          <span className={styles.error} style={{ gridRowStart: 1 }}>
            {errors.firstName}
          </span>
          <span className={styles.error} style={{ gridRowStart: 1 }}>
            {errors.lastName}
          </span>
          <span className={styles.error} style={{ gridRowStart: 1 }}>
            {errors.phoneNumber}
          </span>
        </span>
        <span className={styles.address}>
          <Input
            className={classNames(styles.half, styles.input)}
            autoComplete="off"
            placeholder="Address"
            id="address"
            value={formData.address || ""}
            onChange={(e) => handleForm(e)}
          />
          <Input
            className={classNames(styles.quarter, styles.input)}
            autoComplete="off"
            placeholder="City"
            id="city"
            value={formData.city || ""}
            onChange={(e) => handleForm(e)}
          />
          <Input
            className={classNames(styles.eighth, styles.input)}
            autoComplete="off"
            placeholder="State"
            id="state"
            value={formData.state || ""}
            onChange={(e) => handleForm(e)}
          />
          <Input
            className={classNames(styles.eighth, styles.input)}
            autoComplete="off"
            placeholder="Zip Code"
            id="zipCode"
            value={formData.zipCode || ""}
            onChange={(e) => handleForm(e)}
          />
        </span>
        <span
          style={{
            height: "10px",
            display: "grid",
            gridAutoColumns: "50% 25% 12% 12%",
            width: "100%",
            color: "red",
          }}
        >
          <span className={styles.error} style={{ gridRowStart: 1 }}>
            {errors.address}
          </span>
          <span className={styles.error} style={{ gridRowStart: 1 }}>
            {errors.city}
          </span>
          <span className={styles.error} style={{ gridRowStart: 1 }}>
            {errors.state}
          </span>
          <span className={styles.error} style={{ gridRowStart: 1 }}>
            {errors.zipCode}
          </span>
        </span>
      </div>
      <div className={styles.problemType}>
        <span className={styles.label}>Type of Problem</span>
        <span className={classNames(styles.third, styles.select)}>
          <Select
            options={["Harassment", "Violence", "Hate Speech/Racism", "Other"]}
            placeholder={formData.problemType}
            onChange={(val) => handleForm(val, "problemType")}
          />
        </span>
        <span className={styles.error}>{errors.problemType}</span>
      </div>
      <div className={styles.problemLocation}>
        <span className={styles.third}>
          <span className={styles.label}>Location of Problem</span>
          <span className={styles.select}>
            <Select
              options={["In Person", "Online Platform", "Event", "Meeting"]}
              placeholder={formData.problemLocation}
              onChange={(val) => handleForm(val, "problemLocation")}
            />
          </span>
          <span className={styles.error}>{errors.problemLocation}</span>
        </span>
        <span className={styles.third}>
          <span className={styles.label}>Sub-Location*</span>
          <span className={styles.select}>
            <Select
              options={["CommuniKate", "DreamStream", "Social Media", "Email"]}
              placeholder={formData.subLocation}
              onChange={(val) => handleForm(val, "subLocation")}
            />
          </span>
        </span>
      </div>
      <div className={styles.eventDescription}>
        <span className={styles.label}>Description of Event</span>
        <TextArea
          className={styles.textArea}
          autoComplete="off"
          id="description"
          value={formData.description || ""}
          onChange={(e) => handleForm(e)}
        />
      </div>
      <span className={styles.error}>{errors.description}</span>

      <div className={styles.startDate}>
        <div className={styles.label}>Start Date</div>
        <div className={styles.dateSelects}>
          <div className={styles.select}>
            <Select
              options={months}
              placeholder={formData.startDate.month}
              onChange={(e) => handleForm(e, "startDate.month")}
            />
          </div>
          <div className={styles.select}>
            <Select
              options={daysInMonth}
              placeholder={formData.startDate.day}
              disabled={daysInMonth.length === 0}
              onChange={(e) => handleForm(e, "startDate.day")}
            />
          </div>
          <div className={styles.select}>
            <Select
              options={yearList}
              placeholder={formData.startDate.year}
              disabled={yearList.length === 0}
              onChange={(e) => handleForm(e, "startDate.year")}
            />
          </div>
        </div>
      </div>
      <span className={styles.error}>{errors.startDate}</span>
      <div className={styles.evidence}>
        <span className={styles.label}>Evidence</span>
        <label htmlFor="evidence" className={styles.fileUpload}>
          {fileList.length === 0
            ? "Attach Files"
            : fileList.map((name, index) =>
                index === fileList.length - 1 ? `${name}` : `${name}, `
              )}
          <span className={styles.uploadIcon}>
            <Upload />
          </span>
        </label>
        <input
          className={styles.fileUpload}
          type="file"
          accept="image/png, image/gif, image/jpeg, application/pdf, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          id="evidence"
          onChange={(e) => handleForm(e, "evidence")}
          multiple
        />
      </div>
      <span className={styles.error}>{errors.evidence}</span>
      <div className={styles.complaintAgainst}>
        <span className={styles.label}>Complaint Against</span>
        <span className={styles.loader}>
          <div>{complaintIsLoading && <Loader size={35} />}</div>
        </span>
        <span className={styles.nameAndNumber}>
          <span className={classNames(styles.third, styles.suggestionInput)}>
            <SuggestionInput
              value={formData.complaintAgainst.firstName || ""}
              autoFocus={false}
              minLength={3}
              debounceTime={500}
              fetchSuggestions={setSearchTerm}
              suggestions={searchResults}
              isLoading={isLoading}
              placeholder="First Name"
              id="complaintAgainstFirstName"
              onChange={(e) =>
                handleForm(e.target.value, "complaintAgainst.firstName")
              }
            >
              {(item, closePanel) => {
                const [, label, rest] = item.display.match(/^(.+):(.*)$/);

                return (
                  <div key={item.contactPK} className={styles.iboListItem}>
                    <span
                      onClick={(e) => {
                        handleFillForm(item, false);
                        setSearchTerm("");
                        closePanel();
                      }}
                    >
                      {item.firstName} {item.lastName} ({item.iboNumber})
                    </span>
                  </div>
                );
              }}
            </SuggestionInput>
          </span>
          <span className={styles.third}>
            <Input
              className={styles.input}
              autoComplete="off"
              placeholder="Last Name"
              id="lastName"
              value={formData.complaintAgainst.lastName || ""}
              onChange={(e) =>
                handleForm(e.target.value, "complaintAgainst.lastName")
              }
            />
          </span>
          <PhoneInput
            className={classNames(styles.third, styles.input)}
            autoComplete="off"
            placeholder="Phone Number"
            id="phoneNumber"
            value={formData.complaintAgainst.phoneNumber || ""}
            onChange={(e) =>
              handleForm(e.target.value, "complaintAgainst.phoneNumber")
            }
          />
        </span>
        <span
          style={{
            height: "10px",
            display: "grid",
            gridAutoColumns: "34%",
            width: "100%",
            color: "red",
          }}
        >
          <span className={styles.error} style={{ gridRowStart: 1 }}>
            {errors.complaintAgainst.firstName}
          </span>
          <span className={styles.error} style={{ gridRowStart: 1 }}>
            {errors.complaintAgainst.lastName}
          </span>
          <span className={styles.error} style={{ gridRowStart: 1 }}>
            {errors.complaintAgainst.phoneNumber}
          </span>
        </span>
        <span className={styles.address}>
          <Input
            className={classNames(styles.half, styles.input)}
            autoComplete="off"
            placeholder="Address"
            id="address"
            value={formData.complaintAgainst.address || ""}
            onChange={(e) =>
              handleForm(e.target.value, "complaintAgainst.address")
            }
          />
          <Input
            className={classNames(styles.quarter, styles.input)}
            autoComplete="off"
            placeholder="City"
            id="city"
            value={formData.complaintAgainst.city || ""}
            onChange={(e) =>
              handleForm(e.target.value, "complaintAgainst.city")
            }
          />
          <Input
            className={classNames(styles.eighth, styles.input)}
            autoComplete="off"
            placeholder="State"
            id="state"
            value={formData.complaintAgainst.state || ""}
            onChange={(e) =>
              handleForm(e.target.value, "complaintAgainst.state")
            }
          />
          <Input
            className={classNames(styles.eighth, styles.input)}
            autoComplete="off"
            placeholder="Zip Code"
            id="zipCode"
            value={formData.complaintAgainst.zipCode || ""}
            onChange={(e) =>
              handleForm(e.target.value, "complaintAgainst.zipCode")
            }
          />
        </span>
        <span
          style={{
            height: "15px",
            display: "inline-grid",
            gridAutoColumns: "50% 25% 12% 12%",
            width: "100%",
            color: "red",
          }}
        >
          <span className={styles.error} style={{ gridRowStart: 1 }}>
            {errors.complaintAgainst.address}
          </span>
          <span className={styles.error} style={{ gridRowStart: 1 }}>
            {errors.complaintAgainst.city}
          </span>
          <span className={styles.error} style={{ gridRowStart: 1 }}>
            {errors.complaintAgainst.state}
          </span>
          <span className={styles.error} style={{ gridRowStart: 1 }}>
            {errors.complaintAgainst.zipCode}
          </span>
        </span>
      </div>
      <div className={styles.problemLocation}>
        <span style={{ display: "flex", width: "35%" }}>
          <span className={styles.label}>Is this person a Member of WWG?</span>
          <span className={styles.select}>
            <Select
              options={["Yes", "No"]}
              placeholder={formData.isMember}
              onChange={(val) => handleForm(val, "isMember")}
            />
          </span>
          <span className={styles.error}>{errors.isMember}</span>
        </span>
        <span style={{ display: "flex" }}>
          <span className={styles.label}>
            Is this person perceived to have violated their WWG agreement?
          </span>
          <span
            className={styles.select}
            style={{ display: "flex", width: "50%" }}
          >
            <Select
              options={["Yes", "No"]}
              placeholder={formData.hasViolated}
              onChange={(val) => handleForm(val, "hasViolated")}
            />
          </span>
          <span className={styles.error}>{errors.hasViolated}</span>
        </span>
      </div>
      <div className={styles.problemLocation}>
        <span style={{ display: "flex", width: "88%" }}>
          <span className={styles.label}>
            Does this issue stem from the upline?
          </span>
          <span
            className={styles.select}
            style={{ display: "flex", width: "40%" }}
          >
            <Select
              options={["Yes", "No"]}
              placeholder={formData.fromUpline}
              onChange={(val) => handleForm(val, "fromUpline")}
            />
          </span>
        </span>
      </div>
      <span className={styles.error}>{errors.fromUpline}</span>
      <div className={styles.buttonContainer}>
        <Button className={styles.cancelButton} onClick={() => handleCancel()}>
          Cancel
        </Button>
        <Button className={styles.createButton} onClick={() => handleSubmit()}>
          CREATE
        </Button>
      </div>
    </ReactModal>
  );
}

CreateNewReportModal.propTypes = {
  /**Function called when the create button is clicked */
  onCreate: PropTypes.func,
  /**Getter to determine if modal is deisplayed */
  isOpen: PropTypes.bool,
  /**Callback function for when the modal is closed */
  onClose: PropTypes.func,
};
